import { baseUrl, httpClient } from '../providers/AppDataProvider';
import { listApprovals, type ApprovalRow } from './vaultService';

/**
 * XBIT-4870: the Operations queue's BFF (xbit-admin-service XBIT-4869, `src/modules/vault/operations/`). Every route and
 * field name below is the BFF's; amounts are decimal STRINGS (6 dp) and are never turned into numbers.
 */
const API_PATH = `${baseUrl}/vault/operations`;

/** The console's status vocabulary (ops-console-ux rule 3); the key is the BFF's, colour + words are this app's. */
export type OperationStatus =
  | 'NEEDS_ACTION'
  | 'WAITING_SECOND_APPROVAL'
  | 'IN_PROGRESS'
  | 'WAITING_AUTOMATIC'
  | 'DONE'
  | 'CANCELLED'
  | 'STOPPED'
  | 'HELD';

export type ProposalStatus = 'AWAITING_APPROVAL' | 'APPLIED' | 'CANCELLED';

/** `AdminNamesInterceptor`: a sibling `<field>Admin` beside every operator id. `known` false = "Unknown admin (id…)". */
export interface AdminRef {
  id: string;
  name: string;
  role: string | null;
  known: boolean;
}

/** The operator ids of a proposal and the names the BFF resolved for them. */
export interface ProposalActors {
  proposedBy: string;
  proposedByAdmin?: AdminRef;
  proposedAt: string;
  approvedBy: string | null;
  approvedByAdmin?: AdminRef;
  approvedAt: string | null;
  cancelledBy: string | null;
  cancelledByAdmin?: AdminRef;
  cancelledAt: string | null;
}

/** What every list answers beside its rows. */
export interface ListMeta {
  total: number;
  /** A queue was cut at the page cap: "Showing the first N". */
  truncated?: boolean;
  /** false: the list changed while it was read — refresh. */
  consistent?: boolean;
  checkedAt: string;
}

export interface QueueList<T> extends ListMeta {
  items: T[];
}

// ---- Frozen vaults ----

export interface FreezeCause {
  code: string;
  scope: 'VAULT' | 'ALL_VAULTS';
  originVaultId: string;
  breakValuedAt: string;
  breakAttemptSeq: number;
  frozenAt: string;
  uxStatus: OperationStatus;
  proposal: {
    id: string;
    status: ProposalStatus;
    proposedBy: string;
    proposedByAdmin?: AdminRef;
    proposedAt: string;
    youProposed: boolean;
  } | null;
}

export interface Freeze {
  vaultId: string;
  frozenSince: string;
  subscriptionsFrozen: boolean;
  redemptionsFrozen: boolean;
  causes: FreezeCause[];
  asOf: string;
  status: 'FROZEN';
  uxStatus: OperationStatus;
  whatHappened: string;
  slaDueAt: null;
  autoResolvesAt: null;
}

// ---- Payouts needing a decision ----

export interface WorkOrderResolution extends ProposalActors {
  id: string;
  orderId: string;
  outcome: 'CONFIRMED' | 'FAILED';
  txHash: string | null;
  evidenceRef: string;
  note: string;
  status: ProposalStatus;
  requestId: string;
  uxStatus: OperationStatus;
  youProposed: boolean;
}

export type WorkOrderNextStepKind =
  | 'PROPOSE_RESOLUTION'
  | 'WAIT_FOR_SECOND_APPROVER'
  | 'APPROVE_RESOLUTION'
  | 'NONE';

export interface WorkOrderItem {
  orderId: string;
  vaultId: string | null;
  redemptionRecordId: string | null;
  amount: string | null;
  destination: string;
  createdAt: string;
  waitingSince: string;
  status: string;
  uxStatus: OperationStatus;
  whatHappened: string;
  slaDueAt: null;
  autoResolvesAt: null;
  resolution: WorkOrderResolution | null;
  nextStep: {
    kind: WorkOrderNextStepKind;
    /** true for a live FAILED resolution: approving lets the payout be paid again. */
    moneyMoves: boolean;
    confirmation?: string;
  };
  technical: {
    lane: string;
    stuckReason: string | null;
    activityId: string | null;
    activityLive: boolean;
  };
}

export interface LedgerTransfer {
  txHash: string;
  sender: string;
  destination: string;
  amount: string | null;
  observedAt: string;
}

export interface EvidenceVerdict {
  accepted: boolean;
  code: string | null;
  reason: string | null;
}

/** `GET /work-orders/:id`: the engine's evidence plus the resolution history. */
export interface WorkOrderDetail {
  evidence: {
    orderId: string;
    status: string;
    amount: string | null;
    /** What the signed transfer carries (on the sendAsset lane: amount − the 1 USDC fee); `amount` is what the order owes. */
    sentAmount: string | null;
    /** The account the payout leaves, as the engine records it. */
    source?: string | null;
    destination: string;
    venue: {
      complete: boolean;
      readError: string | null;
      candidates: LedgerTransfer[];
      confirming: LedgerTransfer | null;
    };
    confirm: EvidenceVerdict | null;
    fail: EvidenceVerdict | null;
  };
  resolution: WorkOrderResolution | null;
  history: WorkOrderResolution[];
  uxStatus: OperationStatus;
  nextStep: WorkOrderItem['nextStep'];
  whatHappened: string;
  checkedAt: string;
}

// ---- Escalated redemptions ----

export type ClosurePath = 'SETTLE' | 'RELEASE';

export interface ClosurePreview {
  path: ClosurePath;
  refusal: string | null;
  paid: string | null;
  fee: string | null;
  net: string | null;
  slippage: string | null;
}

export interface ClosureProposal extends ProposalActors {
  id: string;
  instructionId: string;
  vaultId: string;
  path: ClosurePath;
  recovered: string | null;
  closedFraction: string | null;
  overrideQuantFigures: boolean;
  evidenceRef: string;
  note: string;
  source: 'ADMIN' | 'CLI';
  status: ProposalStatus;
  uxStatus: OperationStatus;
  youProposed: boolean;
}

export interface ClosureItem {
  instructionId: string;
  vaultId: string;
  holderId: string;
  /** Not served by the BFF today (it resolves admins, not holders); shown when it is. */
  holderName?: string | null;
  escalatedAt: string;
  requested: string | null;
  claim: string | null;
  quantRecovered: string | null;
  quantClosedFraction: string | null;
  ledgerAdjusted: boolean;
  status: 'ESCALATED';
  uxStatus: OperationStatus;
  whatHappened: string;
  slaDueAt: string;
  slaOverdue: boolean;
  autoResolvesAt: null;
  settle: ClosurePreview;
  release: ClosurePreview;
  resolution: ClosureProposal | null;
  nextStep: {
    kind: 'PROPOSE_RESOLUTION' | 'WAIT_FOR_SECOND_APPROVER' | 'APPROVE_RESOLUTION';
    moneyMoves: true;
    path?: ClosurePath;
    recovered?: string | null;
    closedFraction?: string | null;
    /** What the approval books, when the proposal carries the quant's own figures; null = not previewed. */
    booking?: ClosurePreview | null;
    bookingPreviewed?: boolean;
  };
  technical: { escalationReason: string; quantFinalStatus: string | null };
}

// ---- Transfers to refund ----

export interface QuarantineRefund extends ProposalActors {
  id: string;
  transferId: string;
  vaultId: string;
  sender: string;
  amount: string | null;
  fee: string | null;
  payoutAmount: string | null;
  evidenceRef: string;
  note: string;
  path: 'APPROVAL' | 'AUTO';
  status: ProposalStatus;
  uxStatus: OperationStatus;
  youProposed: boolean;
}

export type TransferNextStepKind =
  | 'PROPOSE_REFUND'
  | 'WAIT_FOR_SECOND_APPROVER'
  | 'APPROVE_REFUND'
  | 'RELEASE_HOLD'
  | 'WAIT_OR_REFUND_NOW'
  | 'REFUND_NOT_POSSIBLE';

export interface RefundLeg {
  fromAddress: string | null;
  toAddress: string | null;
  amount: string | null;
}

export interface TransferItem {
  transferId: string;
  vaultId: string;
  arrivedAt: string;
  ageSeconds: number;
  amount: string | null;
  status: 'QUARANTINED';
  uxStatus: OperationStatus;
  whatHappened: string;
  slaDueAt: null;
  /** The engine's own refund time; null when automatic refunds are off, it is held, or a proposal exists. */
  autoResolvesAt: string | null;
  hold: {
    reason: string | null;
    heldBy: null;
    heldAt: null;
    heldByKnown: boolean;
    lastConsoleHold: {
      heldBy: string;
      heldByAdmin?: AdminRef;
      heldAt: string | null;
    } | null;
  } | null;
  refundProposal: QuarantineRefund | null;
  refundPreviewUnavailable: boolean;
  refund: {
    vaultToCold: RefundLeg;
    toSender: RefundLeg;
    fee: string | null;
  } | null;
  nextStep: { kind: TransferNextStepKind; moneyMoves: boolean };
  technical: { reason: string; binding: string; liveProposalId: string | null };
}

export interface TransferList extends QueueList<TransferItem> {
  /** Vaults the engine could not answer; the rest are served. */
  failedVaults: string[];
  refundFee: string | null;
}

// ---- History ----

export type HistoryQueue = 'work-orders' | 'closures' | 'quarantine';
export type HistoryStatus = 'APPLIED' | 'CANCELLED';

export interface HistoryRow extends ProposalActors {
  queue: HistoryQueue;
  id: string;
  subjectId: string;
  vaultId: string | null;
  status: ProposalStatus;
  uxStatus: OperationStatus;
  /** CONFIRMED | FAILED, SETTLE | RELEASE, APPROVAL | AUTO. */
  kind: string;
  amount: string | null;
  doneAt: string | null;
  evidenceRef: string;
  note: string;
}

export interface HistoryList extends QueueList<HistoryRow> {
  /** Queues that had more than the newest rows the BFF reads. */
  capped: HistoryQueue[];
}

export interface HistoryQuery {
  queue?: HistoryQueue;
  status?: HistoryStatus;
  vaultId?: string;
  offset?: number;
  limit?: number;
}

// ---- request bodies (the BFF's DTO field names) ----

export interface ProposeWorkOrderResolutionInput {
  outcome: 'CONFIRMED' | 'FAILED';
  /** CONFIRMED only. */
  txHash?: string;
  evidenceRef: string;
  note: string;
  requestId: string;
}

export interface ProposeClosureResolutionInput {
  path: ClosurePath;
  recovered: string;
  closedFraction: string;
  overrideQuantFigures?: boolean;
  evidenceRef: string;
  note: string;
  requestId: string;
}

export interface ProposeQuarantineRefundInput {
  evidenceRef: string;
  note?: string;
  requestId: string;
}

const get = async <T>(url: string): Promise<T> => {
  const { json } = await httpClient(url, { method: 'GET' });
  return json as T;
};

const post = async <T>(url: string, body?: unknown): Promise<T> => {
  const { json } = await httpClient(url, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  return json as T;
};

const enc = encodeURIComponent;

const query = (params: Record<string, string | number | undefined>): string => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  return entries.length ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)]))}` : '';
};

/** `GET /freezes` — every vault with an open reconciliation freeze. */
export const listFreezes = (vaultId?: string) =>
  get<QueueList<Freeze>>(`${API_PATH}/freezes${query({ vaultId })}`);

/** `GET /work-orders` — the UNRESOLVED payouts, longest-waiting first. */
export const listWorkOrders = () => get<QueueList<WorkOrderItem>>(`${API_PATH}/work-orders`);

/** `GET /work-orders/:id` — the engine's evidence (venue candidates to pick a txHash from) and the history. */
export const getWorkOrder = (orderId: string) =>
  get<WorkOrderDetail>(`${API_PATH}/work-orders/${enc(orderId)}`);

/** `POST /work-orders/:id/resolutions` — nothing moves until a second, distinct operator approves. */
export const proposeWorkOrderResolution = (
  orderId: string,
  input: ProposeWorkOrderResolutionInput
) => post<WorkOrderResolution>(`${API_PATH}/work-orders/${enc(orderId)}/resolutions`, input);

/** `POST /work-orders/:id/resolutions/:proposalId/approve` (step-up). */
export const approveWorkOrderResolution = (orderId: string, proposalId: string) =>
  post<WorkOrderResolution>(
    `${API_PATH}/work-orders/${enc(orderId)}/resolutions/${enc(proposalId)}/approve`
  );

/** `POST /work-orders/:id/resolutions/:proposalId/cancel`. */
export const cancelWorkOrderResolution = (orderId: string, proposalId: string) =>
  post<WorkOrderResolution>(
    `${API_PATH}/work-orders/${enc(orderId)}/resolutions/${enc(proposalId)}/cancel`
  );

/** `GET /closures` — the ESCALATED redemption close-outs, oldest escalation first, with their SLA. */
export const listClosures = () => get<QueueList<ClosureItem>>(`${API_PATH}/closures`);

/** `POST /closures/:id/resolutions` (`:id` = the instruction id). */
export const proposeClosureResolution = (
  instructionId: string,
  input: ProposeClosureResolutionInput
) => post<ClosureProposal>(`${API_PATH}/closures/${enc(instructionId)}/resolutions`, input);

/** `POST /closures/:id/resolutions/:proposalId/approve` (step-up) — the engine books the resolution. */
export const approveClosureResolution = (instructionId: string, proposalId: string) =>
  post<ClosureProposal>(
    `${API_PATH}/closures/${enc(instructionId)}/resolutions/${enc(proposalId)}/approve`
  );

/** `POST /closures/:id/resolutions/:proposalId/cancel`. */
export const cancelClosureResolution = (instructionId: string, proposalId: string) =>
  post<ClosureProposal>(
    `${API_PATH}/closures/${enc(instructionId)}/resolutions/${enc(proposalId)}/cancel`
  );

/** `GET /quarantined-transfers[?vaultId]` — no vaultId = every vault. */
export const listQuarantinedTransfers = (vaultId?: string) =>
  get<TransferList>(`${API_PATH}/quarantined-transfers${query({ vaultId })}`);

const transferPath = (vaultId: string, transferId: string) =>
  `${API_PATH}/vaults/${enc(vaultId)}/quarantined-transfers/${enc(transferId)}`;
const refundPath = (vaultId: string, proposalId: string) =>
  `${API_PATH}/vaults/${enc(vaultId)}/refund-proposals/${enc(proposalId)}`;

/** `POST /vaults/:id/quarantined-transfers/:transferId/refund-proposals`. */
export const proposeQuarantineRefund = (
  vaultId: string,
  transferId: string,
  input: ProposeQuarantineRefundInput
) => post<QuarantineRefund>(`${transferPath(vaultId, transferId)}/refund-proposals`, input);

/** `POST /vaults/:id/refund-proposals/:proposalId/approve` (step-up) — the refund moves money. */
export const approveQuarantineRefund = (vaultId: string, proposalId: string) =>
  post<QuarantineRefund>(`${refundPath(vaultId, proposalId)}/approve`);

/** `POST /vaults/:id/refund-proposals/:proposalId/cancel`. */
export const cancelQuarantineRefund = (vaultId: string, proposalId: string) =>
  post<QuarantineRefund>(`${refundPath(vaultId, proposalId)}/cancel`);

/** `POST /vaults/:id/quarantined-transfers/:transferId/hold` — one operator stops the automatic refund; no money moves. */
export const holdQuarantinedTransfer = (vaultId: string, transferId: string, reason: string) =>
  post<unknown>(`${transferPath(vaultId, transferId)}/hold`, { reason });

/** `POST /vaults/:id/quarantined-transfers/:transferId/release-hold` (step-up) — re-arms the automatic refund. */
export const releaseQuarantineHold = (vaultId: string, transferId: string) =>
  post<unknown>(`${transferPath(vaultId, transferId)}/release-hold`);

/** `GET /history[?queue&status&vaultId&offset&limit]` — the proposals that ended, newest first. */
export const listHistory = (filter: HistoryQuery = {}) =>
  get<HistoryList>(`${API_PATH}/history${query({ ...filter })}`);

// ---- XBIT-4950: Waiting for signers (the former Approvals page) ----

/** The BFF's largest page (`1 ≤ limit ≤ 200`); at most this many pages are read, and a longer list is reported as truncated. */
const SIGNERS_PAGE = 200;
const SIGNERS_MAX_PAGES = 5;

/**
 * `GET /vault/approvals` as one Operations list. The route lists every order only a person can move on: AWAITING_QUORUM
 * (waits for signers) and UNRESOLVED (signed, outcome unknown). UNRESOLVED orders are decided under "Payouts needing a
 * decision", so they are dropped here: counted in both tabs they would keep this one from ever reading 0. The route is not
 * part of the Operations BFF and carries no `checkedAt`: the time of this read stands in for it. Read-only: nothing is
 * signed in the console.
 */
export async function listWaitingForSigners(): Promise<QueueList<ApprovalRow>> {
  const rows: ApprovalRow[] = [];
  let read = 0;
  let total = 0;
  for (let i = 0; i < SIGNERS_MAX_PAGES; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const page = await listApprovals({ offset: read, limit: SIGNERS_PAGE });
    rows.push(...page.items.filter((row) => row.status !== 'UNRESOLVED'));
    read += page.items.length;
    total = page.total;
    if (page.items.length === 0 || read >= total) break;
  }
  const truncated = read < total;
  return {
    items: rows,
    // a cut list does not know how many of the rest are UNRESOLVED: the count is what was read
    total: rows.length,
    truncated,
    consistent: true,
    checkedAt: new Date().toISOString(),
  };
}

/**
 * XBIT-5003: the held half of `IN_FLIGHT`. A hold keeps the order's status and writes no transition, so a payout stopped at
 * SIGNED or AWAITING_FLOAT moves in no queue; `outcome=IN_FLIGHT` is the only group it appears in. XBIT-5159: an
 * AWAITING_FLOAT payout is kept with or without a hold, because Hot cannot cover it and it showed nowhere else. Other rows
 * the engine is simply carrying are dropped, and listing them would bury these. Read-only.
 */
export async function listHeldOrders(): Promise<QueueList<ApprovalRow>> {
  const rows: ApprovalRow[] = [];
  let read = 0;
  let total = 0;
  for (let i = 0; i < SIGNERS_MAX_PAGES; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const page = await listApprovals({ offset: read, limit: SIGNERS_PAGE }, 'IN_FLIGHT');
    rows.push(...page.items.filter((row) => row.heldBy || row.status === 'AWAITING_FLOAT'));
    read += page.items.length;
    total = page.total;
    if (page.items.length === 0 || read >= total) break;
  }
  return {
    items: rows,
    // a cut list does not know how many of the rest are kept: the count is what was read
    total: rows.length,
    truncated: read < total,
    consistent: true,
    checkedAt: new Date().toISOString(),
  };
}

// ---- XBIT-4993: treasury lane pause ----

export type LanePauseScope = 'USER_OUTFLOW' | 'COMPANY_INTERNAL';

/**
 * One scope's pause. The engine records a reason and who paused, and exposes neither on any read: this carries only what the BFF
 * serves. `at` is the last pause or resume (null = the scope has no history); `resumeProposedBy` is set while a resume waits.
 */
export interface LanePauseState {
  scope: LanePauseScope;
  paused: boolean;
  /** A decimal string, never a number. */
  eventId: string;
  at: string | null;
  resumeProposedBy: string | null;
}

const LANE_PAUSES_PATH = `${baseUrl}/vault/approvals/lane-pauses`;

/** `GET /vault/approvals/lane-pauses` — one state per scope. */
export const listLanePauses = () => get<LanePauseState[]>(LANE_PAUSES_PATH);

/** `POST /vault/approvals/lane-pauses` — one operator pauses; no money moves. */
export const pauseLane = (scope: LanePauseScope, reason: string) =>
  post<LanePauseState>(LANE_PAUSES_PATH, { scope, reason });

/** `POST /vault/approvals/lane-pauses/resume` — proposes a resume; the scope stays paused until a distinct operator approves. */
export const proposeLaneResume = (scope: LanePauseScope, reason: string) =>
  post<LanePauseState>(`${LANE_PAUSES_PATH}/resume`, { scope, reason });

/** `POST /vault/approvals/lane-pauses/resume/approve` — refused (VAULT_LANE_RESUME_SAME_APPROVER) for the proposer. */
export const approveLaneResume = (scope: LanePauseScope) =>
  post<LanePauseState>(`${LANE_PAUSES_PATH}/resume/approve`, { scope });
